import { QueryKeys } from "@/constants/Query";
import { withMultipleEncountersHandler } from "@/features/Patient/MedicalRecords/MedicalHistory/useMedicalHistoryErrorHandler";
import type { ApiError } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { Encounter } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type { Condition, Encounter as FhirEncounter } from "fhir/r4";

export interface AddDiagnosisToVisitParams {
  diagnosisSnomedCode: string;
  diagnosis: string;
}

interface AddDiagnosisToVisitResponse {
  condition: Condition;
  encounter: FhirEncounter;
}

export const useAddDiagnosisToVisit = ({
  patientId,
  motherEncounterId,
  onSuccess,
  onError,
}: {
  patientId: string;
  motherEncounterId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<AddDiagnosisToVisitResponse, AddDiagnosisToVisitParams>(
    {
      mutationOptions: {
        mutationKey: QueryKeys.Patient.Conditions.ADD_DIAGNOSIS({
          patientId,
          encounterId: motherEncounterId,
        }),
        mutationFn: (params) =>
          axiosInstance.post(
            `/charting/encounter-charting/${motherEncounterId}/diagnosis/add`,
            {
              diagnosis: {
                ...params,
                diagnosisRole: Encounter.DiagnosisRole.values.CC,
              },
            },
          ),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
                patientId,
              }),
          });
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
          });
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Distribution.AVAILABLE_ACTIONS({
              patientId,
              encounterId: motherEncounterId,
            }),
          });
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTERS({
              patientId,
            }),
          });
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.EpisodesOfCare.LIST(patientId),
          });
          onSuccess?.();
        },
        onError: withMultipleEncountersHandler(onError),
      },
    },
  );
};
